import { useState, useEffect } from "react";
import DataTable from "../components/common/DataTable";
import Skeleton from "../components/common/Skeleton";
import MyPagination from "../components/common/Pagination";
import FilterDrawer from "../modal/FilterDrawer";
import { EntityRegistry } from "../../config/entityRegistry";
import { fetchEntityList, exportEntityToExcel } from "../../config/entityService";
import api from "../../shared/AxiosConfig";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import ApplicationVisibilityPanel from "../components/ApplicationVisibilityPanel";


export default function EntityListPage({ type }) {

    const config = EntityRegistry[type];
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);

    const [filters, setFilters] = useState({});
    const [filterOpen, setFilterOpen] = useState(false);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const perPage = 10;

    const [page, setPage] = useState(1);

    const [fadeIn, setFadeIn] = useState(false);


    const resetPage = () => setPage(1);

    const resetAll = () => {
        setFilters({});
        setPage(1);
        setData([]);
        setTotal(0);
        setLoading(true);
    };


    useEffect(() => {
        setFadeIn(false);

        const t = setTimeout(() => {
            setFadeIn(true);
        }, 50);

        return () => clearTimeout(t);
    }, [type, location.pathname]);



    // Filter veya type değişince sayfa başa dönsün
    useEffect(() => {
        setPage(1);
    }, [filters, type]);

    useEffect(() => {
        resetAll();
    }, [type, location.pathname]);

    // ESC ile drawer kapatma
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key !== "Escape") return;
            if (filterOpen) setFilterOpen(false);
            else if (drawerOpen) setDrawerOpen(false);
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [filterOpen, drawerOpen]);


    // Fetch Trigger
    useEffect(() => {
        loadData();
    }, [page, filters, type, location.pathname]);



    // ---------------- FETCH LIST ----------------
    async function loadData() {
        setLoading(true);

        try {
            const res = await fetchEntityList(config, {
                page,
                limit: perPage,
                filters
            });


            setData(res?.data?.map(config.mapper) || []);

            setTotal(res?.pagination?.total || 0);




        } catch (err) {
            console.error("List load failed", err);
            setData([]);
            setTotal(0);
        }

        setLoading(false);
    }

    // ---------------- EXPORT ----------------
    async function handleExport() {
        if (!config.exportEndpoint) return;
        setExportLoading(true);
        try {
            await exportEntityToExcel(config, filters);
            toast.success("Excel dosyası indiriliyor");
        } catch (err) {
            console.error("Export failed", err);
            toast.error(err?.response?.data?.message || "Excel indirilemedi");
        } finally {
            setExportLoading(false);
        }
    }

    // ---------------- RENDER ----------------
    return (
        <div className={`page-container fade-wrapper ${fadeIn ? "show" : ""}`}>


            <div className="table-card">

                {/* HEADER */}
                <div className="  table-header">
                        <h2 className="table-title">{config.title}</h2>

                        {!loading && (
                            <div className="table-header-actions">
                                {(type === "enrollment" || type === "term") && (
                                    <button
                                        className="filter-button-modern"
                                        onClick={() => {
                                            setDrawerOpen(true);
                                            setSelected(null);
                                            setDetailLoading(false);
                                        }}
                                    >
                                        <i className="bi bi-plus-lg me-2"></i>
                                        Yeni
                                    </button>
                                )}

                                {config.exportEndpoint && (
                                    <button
                                        className="filter-button-modern"
                                        onClick={handleExport}
                                        disabled={exportLoading}
                                    >
                                        <i className="bi bi-file-earmark-excel me-2"></i>
                                        {exportLoading ? "Hazırlanıyor..." : "Excel"}
                                    </button>
                                )}

                                {config.filters?.length > 0 && (
                                    <button
                                        className="filter-button-modern"
                                        onClick={() => setFilterOpen(true)}
                                    >
                                        <i className="bi bi-funnel me-2"></i>
                                        Filtrele
                                    </button>
                                )}
                            </div>
                        )}

                </div>

                {type === "application" && <ApplicationVisibilityPanel />}

                {/* TABLE */}
                <DataTable
                    columns={config.columns}
                    data={data}
                    loading={loading}
                    currentPage={page}
                    itemsPerPage={perPage}
                    onRowClick={async (row) => {
                        if (!config.detailComponent) return;

                        setDrawerOpen(true);
                        setSelected(null);
                        setDetailLoading(true);

                        try {
                            const res = await api.get(config.detailEndpoint || config.endpoint, {
                                params: {publicId: row.publicId}
                            });

                            const raw =
                                res?.data?.data ||
                                res?.data?.items?.[0] ||
                                res?.data?.[0] ||
                                res?.data;

                            const detail = config.detailMapper
                                ? config.detailMapper(raw)
                                : raw;

                            setSelected(detail);

                        } catch (err) {
                            console.error("DETAIL FAILED", err);
                        }

                        setDetailLoading(false);
                    }}
                />

                {/* PAGINATION */}
                <div className="mt-3">

                    {loading ? (
                        <div className="pagination-skeleton-wrapper">
                            <Skeleton width={40} height={36}/>
                            <Skeleton width={40} height={36}/>
                            <Skeleton width={40} height={36}/>
                            <Skeleton width={40} height={36}/>
                            <Skeleton width={60} height={36}/>
                        </div>
                    ) : (
                        <MyPagination
                            total={Math.ceil(total / perPage)}
                            current={page}
                            onChangePage={setPage}
                        />
                    )}

                </div>


            </div>

            {/* DETAIL DRAWER */}
            {config.detailComponent && (
                <config.detailComponent
                    open={drawerOpen}
                    data={selected}
                    loading={detailLoading}
                    onClose={(shouldRefresh) => {
                        setDrawerOpen(false);

                        if (shouldRefresh) {
                            loadData(); // 🔥 Listeyi yeniden çek
                        }
                    }}

                />
            )}

            {/* FILTER */}
            <FilterDrawer
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                filters={filters}
                setFilters={setFilters}
                scheme={config.filters}
                resetPage={resetPage}
            />

        </div>
    );
}
